import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Server, Save, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ServerConfigDialog = ({ open, onOpenChange }) => {
  const [serverUrl, setServerUrl] = useState('');
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null); // 'success', 'error', null

  useEffect(() => {
    // Load saved server URL from localStorage
    const savedUrl = localStorage.getItem('crm_server_url');
    if (savedUrl) {
      setServerUrl(savedUrl);
    } else {
      // Default to current backend URL
      setServerUrl(process.env.REACT_APP_BACKEND_URL || '');
    }
  }, [open]);

  const testConnection = async () => {
    if (!serverUrl) {
      toast.error('Please enter a server URL');
      return;
    }

    setTesting(true);
    setConnectionStatus(null);

    try {
      // Test the connection by calling a simple endpoint
      const response = await fetch(`${serverUrl}/api/ip/check`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.ok) {
        setConnectionStatus('success');
        toast.success('Connection successful!');
      } else {
        setConnectionStatus('error');
        toast.error('Server responded but connection may have issues');
      }
    } catch (error) {
      setConnectionStatus('error');
      toast.error('Failed to connect to server');
    } finally {
      setTesting(false);
    }
  };

  const saveSettings = () => {
    if (!serverUrl) {
      toast.error('Please enter a server URL');
      return;
    }

    localStorage.setItem('crm_server_url', serverUrl);
    toast.success('Server URL saved! Please restart the app for changes to take effect.');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Server className="w-4 h-4 text-amber-500" />
            </div>
            Server Configuration
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Configure the backend server URL for the CRM system.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Server URL</Label>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://your-server.com"
                value={serverUrl}
                onChange={(e) => {
                  setServerUrl(e.target.value);
                  setConnectionStatus(null);
                }}
                className="flex-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
              <Button 
                variant="outline" 
                onClick={testConnection}
                disabled={testing}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                {testing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
            {connectionStatus && (
              <div className={`flex items-center gap-2 text-sm ${
                connectionStatus === 'success' ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {connectionStatus === 'success' ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Connection successful
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Connection failed
                  </>
                )}
              </div>
            )}
          </div>

          <div className="bg-slate-700/50 rounded-lg p-3 text-sm text-slate-400">
            <p className="font-medium text-slate-300 mb-1">Note:</p>
            <p>The server URL should point to your deployed backend server. Changes will take effect after restarting the application.</p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600"
              onClick={saveSettings}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServerConfigDialog;
